if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.modalContent.classList.remove('quick-add-modal__content-info--quick-view');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');

        this.fetchQuickViewHTML(productUrl)
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.loadSectionAssets(responseHTML);
            const productElement =
              responseHTML.querySelector('product-info.quick-view') ||
              responseHTML.querySelector('product-info') ||
              responseHTML.querySelector('[id^="shopify-section-"] product-info');

            if (!productElement) {
              throw new Error('Quick view content not found');
            }

            this.modalContent.classList.add('quick-add-modal__content-info--quick-view');
            this.syncQuickViewBackground(productElement);
            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
            if (typeof window.ratingSnippet === 'function') {
              window.ratingSnippet();
            }

            super.show(opener);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      syncQuickViewBackground(productElement) {
        const bg = productElement.style.getPropertyValue('--quick-view-bg');
        const dialog = this.querySelector('.quick-view-modal__content');
        if (!bg || !dialog) return;
        dialog.style.setProperty('--quick-view-bg', bg);
        dialog.style.backgroundColor = bg;
      }

      async fetchQuickViewHTML(productUrl) {
        const sectionsUrl = new URL(productUrl, window.location.origin);
        sectionsUrl.searchParams.set('sections', 'quick-view');

        const sectionsResponse = await fetch(sectionsUrl.toString());
        const contentType = sectionsResponse.headers.get('content-type') || '';
        if (sectionsResponse.ok && contentType.includes('application/json')) {
          const data = await sectionsResponse.json();
          if (data['quick-view']) return data['quick-view'];
        }

        const sectionUrl = new URL(productUrl, window.location.origin);
        sectionUrl.searchParams.set('section_id', 'quick-view');
        const sectionResponse = await fetch(sectionUrl.toString());
        return sectionResponse.text();
      }

      loadSectionAssets(responseHTML) {
        responseHTML.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href || document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;

          const stylesheet = document.createElement('link');
          stylesheet.rel = 'stylesheet';
          stylesheet.href = href;
          document.head.appendChild(stylesheet);
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        if (!sectionId) return;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        const rewriteValue = (value) => value.split(oldId).join(newId);

        const rewriteElement = (el) => {
          Array.from(el.attributes).forEach((attribute) => {
            if (attribute.name === 'class' || attribute.name === 'style') return;
            if (!attribute.value.includes(oldId)) return;
            el.setAttribute(attribute.name, rewriteValue(attribute.value));
          });
        };

        rewriteElement(productElement);
        productElement.querySelectorAll('*').forEach(rewriteElement);

        productElement.querySelectorAll('script').forEach((script) => {
          const text = script.textContent;
          if (!text || !text.includes(oldId)) return;
          script.textContent = text
            .replaceAll(`-${oldId}`, `-${newId}`)
            .replaceAll(`"${oldId}"`, `"${newId}"`)
            .replaceAll(`'${oldId}'`, `'${newId}'`);
        });

        productElement.dataset.originalSection = oldId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
